// ============================================================
//  BEREAN NATION — Firestore Service Layer
//  All real-time database reads/writes go through here.
//  Collections:
//    prayers/         — prayer requests (shared, real-time)
//    annotations/     — per-user Bible highlights & notes
//    users/           — FCM tokens, display names, prefs
//    videos/          — video metadata for CF Readings & BN News
//    notifications/   — push notification log
// ============================================================

import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  onSnapshot, query, orderBy, serverTimestamp,
  getDoc, setDoc, arrayUnion, increment, where, getDocs
} from 'firebase/firestore';
import { db, ensureAuth } from './firebase';

// ── PRAYER REQUESTS ───────────────────────────────────────

/** Subscribe to all prayer requests in real time. Returns unsubscribe fn. */
export function subscribePrayers(onUpdate) {
  const q = query(collection(db, 'prayers'), orderBy('createdAt', 'desc'));
  return onSnapshot(q, snap => {
    const prayers = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    onUpdate(prayers);
  });
}

/** Add a new prayer request. */
export async function addPrayer({ name, request, tags, isAnonymous }) {
  const user = await ensureAuth();
  return addDoc(collection(db, 'prayers'), {
    name: isAnonymous ? 'Anonymous' : (name || 'Anonymous'),
    request,
    tags: tags || [],
    praying: 0,
    answered: false,
    prayedBy: [],
    authorUid: user.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/** Edit an existing prayer request (only your own). */
export async function updatePrayer(id, fields) {
  return updateDoc(doc(db, 'prayers', id), {
    ...fields,
    updatedAt: serverTimestamp(),
  });
}

/** Delete a prayer request (only your own). */
export async function deletePrayer(id) {
  return deleteDoc(doc(db, 'prayers', id));
}

/** Increment the "praying" counter (idempotent per user). */
export async function prayForRequest(prayerId) {
  const user = await ensureAuth();
  const ref  = doc(db, 'prayers', prayerId);
  const snap = await getDoc(ref);
  const data = snap.data();
  if (data.prayedBy && data.prayedBy.includes(user.uid)) return; // already prayed
  return updateDoc(ref, {
    praying:  increment(1),
    prayedBy: arrayUnion(user.uid),
  });
}

/** Toggle answered status. */
export async function markAnswered(prayerId, answered) {
  return updateDoc(doc(db, 'prayers', prayerId), { answered });
}

// ── BIBLE ANNOTATIONS ─────────────────────────────────────

/**
 * Subscribe to a user's annotations for one book+chapter.
 * Returns unsubscribe fn.
 */
export async function subscribeAnnotations(book, chapter, onUpdate) {
  const user = await ensureAuth();
  const q = query(
    collection(db, 'annotations'),
    where('uid', '==', user.uid),
    where('book', '==', book),
    where('chapter', '==', chapter)
  );
  return onSnapshot(q, snap => {
    // Convert to { "verseIndex": { highlight, note } }
    const map = {};
    snap.docs.forEach(d => {
      const data = d.data();
      map[data.verseIndex] = { highlight: data.highlight, note: data.note, docId: d.id };
    });
    onUpdate(map);
  });
}

/** Save or update a highlight / note for a verse. */
export async function saveAnnotation({ book, chapter, verseIndex, highlight, note }) {
  const user = await ensureAuth();
  // Use a deterministic document ID so we can upsert
  const docId = `${user.uid}_${book}_${chapter}_${verseIndex}`;
  return setDoc(doc(db, 'annotations', docId), {
    uid: user.uid,
    book,
    chapter,
    verseIndex,
    highlight: highlight || null,
    note: note || null,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

/** Remove a note / highlight. */
export async function deleteAnnotation(docId) {
  return deleteDoc(doc(db, 'annotations', docId));
}

// ── VIDEOS ────────────────────────────────────────────────

/** Subscribe to videos for a category ('fathers' | 'news'). */
export function subscribeVideos(category, onUpdate) {
  const q = query(
    collection(db, 'videos'),
    where('category', '==', category),
    orderBy('publishedAt', 'desc')
  );
  return onSnapshot(q, snap => {
    const videos = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    onUpdate(videos);
  });
}

/**
 * Seed initial videos (run once from admin).
 * In production, videos are added via the Firebase Console or
 * a Cloud Function triggered when you upload to YouTube/Rumble.
 */
export async function seedVideos(videos) {
  for (const v of videos) {
    await addDoc(collection(db, 'videos'), {
      ...v,
      publishedAt: serverTimestamp(),
    });
  }
}

// ── USER FCM TOKENS ───────────────────────────────────────

/** Save an FCM token so Cloud Functions can send targeted pushes. */
export async function saveFcmToken(token) {
  const user = await ensureAuth();
  await setDoc(doc(db, 'users', user.uid), {
    fcmTokens: arrayUnion(token),
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

/** Save display name preference. */
export async function saveDisplayName(name) {
  const user = await ensureAuth();
  return setDoc(doc(db, 'users', user.uid), {
    displayName: name,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}
